import type { PerformanceFeeStructure } from '@/types/client';

export const getBenchmarkLabel = (val: string | undefined): string => {
  if (!val) return '—';
  if (val === 'BSE500') return 'BSE 500';
  if (val === 'BSE500TRI') return 'BSE 500 TRI';
  if (val === '10YB') return '10Y Bond';
  if (val === 'SPY') return 'SPY ETF';
  if (val === 'SPX') return 'S&P 500';
  return val;
};

// Trimmed, not formatPercentage — a row carries up to five of these, and
// "7.00% → 20.00%" is wider than "7% → 20%" for no extra information. The
// tooltip still shows the padded figures.
const trimPercent = (value: number | string | undefined | null): string | null => {
  const num = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(num)
    ? String(Number(num.toFixed(2)))
    : null;
};

export interface HurdleStep {
  hurdle: string; // the slab's own hurdle, or its benchmark name
  fee: string;    // performance fee charged once that hurdle is cleared
}

/**
 * Pair each performance fee slab's hurdle with the fee it charges, in step order.
 *
 * Each row reads as its own threshold — "7% → 20%" — rather than a range, so
 * nothing has to be inferred from the slab above or below it.
 */
export const buildHurdleSteps = (
  structures: PerformanceFeeStructure[] | undefined
): HurdleStep[] => {
  if (!structures || structures.length === 0) return [];

  return [...structures]
    .sort((a, b) => (a.step_no ?? 0) - (b.step_no ?? 0))
    .map((structure) => {
      const fee = trimPercent(structure.fee_value);
      const feeLabel = fee === null ? '—' : `${fee}%`;

      if (structure.hurdle_type === 'Benchmark') {
        return { hurdle: getBenchmarkLabel(structure.hurdle_benchmark), fee: feeLabel };
      }

      const hurdle = trimPercent(structure.hurdle_value);
      return { hurdle: hurdle === null ? '—' : `${hurdle}%`, fee: feeLabel };
    });
};
